import { observer } from "mobx-react-lite";
import { useEffect, useLayoutEffect, useRef } from "react";
import { Alert, PreviewAlert } from "state/alert.ts";
import { SharingButtons } from "posts-parts/sharing-buttons.tsx";
import { MiniMap } from "./minimap.tsx";
import { uniq } from "lodash";
import { MarkdownText } from "./markdown.tsx";
import { HomeIcon, LinkIcon } from "@heroicons/react/outline";
import copy from "copy-text-to-clipboard";
import toast from "react-hot-toast";
import { ExplorerStore } from "explorer/store.ts";
import { NestedTags, Tag } from "components/nested-tags.tsx";

export const ExplorerDetailsView = observer(function ExplorerDetailsView({
  store,
}: {
  store: ExplorerStore;
}) {
  const post = store.post;
  const loadedCount = store.previewPosts.length;
  const positionInPreviews = post
    ? store.previewPosts.findIndex((p) => p.id === post.id)
    : -1;
  const atEndOfLoaded = !!post && positionInPreviews >= loadedCount - 1;
  const moreAvailable =
    loadedCount < (store.meili.lastPreviewsResponse?.estimatedTotalHits ?? 0);
  // on the last loaded alert, load the next page so Next keeps working
  // (once per page, not on every render)
  useEffect(() => {
    if (atEndOfLoaded && moreAvailable) {
      void store.meili.updatePreviewList(loadedCount);
    }
  }, [atEndOfLoaded, moreAvailable, loadedCount, store]);

  if (!post) {
    return null;
  }
  let prevPreview;
  let nextPreview;
  if (positionInPreviews !== -1) {
    prevPreview = store.previewPosts[positionInPreviews - 1];
    nextPreview = store.previewPosts[positionInPreviews + 1];
  }
  return (
    <DetailsView
      post={post}
      className="pointer-events-auto"
      closeFunction={() => store.setPost(undefined)}
      previousPost={prevPreview}
      nextPost={nextPreview}
      position={positionInPreviews}
      total={store.meili.lastPreviewsResponse?.estimatedTotalHits}
      store={store}
    />
  );
});

function removeHighlight(text: string) {
  return text?.replaceAll(/<\/?em>/g, "");
}

export const MiniPreview = observer(function MiniPreview({
  post,
}: {
  post: PreviewAlert;
}) {
  return (
    <span class={"truncate text-ellipsis whitespace-nowrap text-xs"}>
      {removeHighlight(post.country)}:{" "}
      {removeHighlight(post.title).substring(0, 100)}
    </span>
  );
});

const obctAvailableCountries = [
  "Albania",
  "Austria",
  "Belgium",
  "Bulgaria",
  "Croatia",
  "Cyprus",
  "Czech Republic",
  "Denmark",
  "Estonia",
  "Finland",
  "France",
  "Georgia",
  "Germany",
  "Greece",
  "Hungary",
  "Ireland",
  "Italy",
  "Latvia",
  "Luxembourg",
  "Malta",
  "Moldova",
  "Montenegro",
  "Netherlands",
  "North Macedonia",
  "Poland",
  "Portugal",
  "Romania",
  "Serbia",
  "Slovakia",
  "Slovenia",
  "Spain",
  "Sweden",
  "Turkey",
  "Ukraine",
  "United Kingdom",
];

function ExternalResourceLinks(props: { post: Alert }) {
  const { post } = props;
  return (
    <div>
      <h3 class="mt-8 font-bold text-lg only:hidden">Additional Information</h3>
      <ObctResourceLink
        country={post.regionNames?.[1]}
        labels={post.obct_labels}
      />
      {post.coe_link?.length && (
        <p class="mt-2">
          <a
            href={post.coe_link}
            target="_blank"
            rel="noreferrer"
            class="underline"
          >
            Council of Europe safety of journalists platform alert
          </a>
        </p>
      )}
    </div>
  );
}

function ObctResourceLink(props: { country: string; labels: string[] }) {
  const { country, labels } = props;
  const items: { label: string; slug: string }[] = [];
  if (country) {
    const mappedCountry = country.replace("Czechia", "Czech Republic");
    const encodedCountry = encodeURIComponent(mappedCountry.toLowerCase());
    if (obctAvailableCountries.includes(mappedCountry)) {
      items.push({ label: mappedCountry, slug: encodedCountry });
    }
  }
  if (labels && labels.length > 0) {
    labels.forEach((label) =>
      items.push({
        label,
        slug: encodeURIComponent(label.toLowerCase().replaceAll(" ", "_")),
      }),
    );
  }
  if (items.length < 1) {
    return;
  }

  return (
    <>
      <p class="mt-2">
        The OBCT Resource Centre has more information about{" "}
        {items.map((item, idx) => [
          idx > 0 && (idx == items.length - 1 ? " and " : ", "),
          <a
            key={item.slug}
            href={`https://www.rcmediafreedom.eu/content/search?SearchText=&filter[]=attr_enhanced_tags_lk:${item.slug}&activeFacets[attr_enhanced_tags_lk:Tags]=${item.slug}`}
            target="_blank"
            rel="noreferrer"
            class="underline"
          >
            {item.label}
          </a>,
        ])}
        .
      </p>
    </>
  );
}

export const LegacySubjectsView = observer(function LegacySubjectsView({
  post,
}: {
  post: Alert;
}) {
  return (
    <div>
      {post.who_was_attacked?.length > 0 && (
        <>
          <h3 class="mt-4 font-bold">Who was attacked</h3>
          <NestedTags topSlug="who-was-attacked" tags={post.who_was_attacked} />
        </>
      )}
      {post.type_of_journalist_or_media_actor?.length && (
        <>
          <h3 class="mt-4 font-bold">Type of journalist or media actor:</h3>
          <NestedTags
            topSlug="type-of-journalist-or-media-actor"
            tags={post.type_of_journalist_or_media_actor}
          />
        </>
      )}
      {post.gender?.length && (
        <>
          <h3 class="mt-4 font-bold">Gender</h3>
          <NestedTags topSlug="gender" tags={post.gender} />
        </>
      )}
      {post.employment_status?.length && (
        <>
          <h3 class="mt-4 font-bold">Employment status</h3>
          <NestedTags
            topSlug="employment-status"
            tags={post.employment_status}
          />
        </>
      )}
    </div>
  );
});

export const SubjectsView = observer(function SubjectsView({
  post,
}: {
  post: Alert;
}) {
  const journalists = post.subjects?.filter((s) => s.kind == "journalist");
  const others = post.subjects?.filter((s) => s.kind == "other");
  return (
    <div>
      {post.subjects?.length > 0 ? (
        <>
          <h3 class="mt-4 font-bold">Who was attacked</h3>
          {journalists.length > 0 && (
            <div>
              <div class="font-semibold">
                <Tag tag={journalists[0].who} />
              </div>
              <div class="ml-8 card-text grid grid-cols-[min-content_minmax(min-content,1fr)_minmax(min-content,1fr)_minmax(min-content,1fr)] gap-4">
                <div class="mt-4 text-xs italic">Amount</div>
                <div class="mt-4 text-xs italic">Type of Journalist</div>
                <div class="mt-4 text-xs italic">Gender</div>
                <div class="mt-4 text-xs italic">Employment Status</div>
                {journalists.map((s, index) => (
                  <div class="contents" key={index}>
                    <div>{s.amount}</div>
                    <div>{s.type}</div>
                    <div>{s.gender}</div>
                    <div>{s.employment_status}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {others.length > 0 && (
            <div class={"mt-4 font-semibold"}>
              <Tag tag="Other persons or entities related to media" />
            </div>
          )}
          {others.map((s, index) => (
            <div
              key={index}
              class="ml-8 card-text grid grid-cols-[min-content_max-content] gap-4"
            >
              <div>{s.amount}</div>
              <div>{s.who}</div>
            </div>
          ))}
          {post.subjects
            .filter((s) => s.kind == "general")
            .map((s, index) => (
              <div key={index} class="card-text">
                <Tag tag={s.who} />
              </div>
            ))}
        </>
      ) : (
        <LegacySubjectsView post={post} />
      )}
    </div>
  );
});

export const DetailsView = observer(function DetailsView({
  post,
  className,
  closeFunction,
  nextPost,
  previousPost,
  position,
  total,
  store,
}: {
  post: Alert;
  className: string;
  closeFunction: () => void;
  nextPost?: PreviewAlert;
  previousPost?: PreviewAlert;
  position?: number;
  total?: number;
  store: ExplorerStore;
}) {
  // A modal dialog: the explorer below is inert while it is open (see entrypoints/explorer.tsx),
  // focus moves in on open and back on close, Escape closes it.
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(closeFunction);
  closeRef.current = closeFunction;

  useLayoutEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeRef.current();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      if (previouslyFocused?.isConnected)
        previouslyFocused.focus({ preventScroll: true });
    };
  }, []);

  // also after Previous/Next, so screen readers announce the new alert
  useLayoutEffect(() => {
    dialogRef.current?.focus({ preventScroll: true });
  }, [post.id]);

  return (
    <div
      class={`absolute inset-0 z-aboveallcontent m-0 bg-black/25 md:backdrop-blur-sm ${className}`}
      onClick={(e) => e.target === e.currentTarget && closeFunction()}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="details-view-title"
        tabIndex={-1}
        class={`${store.postLoadingId ? "cursor-progress" : ""} absolute inset-0 z-aboveallcontent m-0 outline-none grid grid-cols-1 grid-rows-[auto_1fr_auto] bg-white p-2 pr-7 shadow-xl dark:bg-black md:bg-white/90 md:dark:bg-black/90 dark:text-white md:mx-auto md:my-10 md:max-w-(--breakpoint-md) md:rounded-modal md:pl-10 md:pr-20 md:pt-10 md:shadow-nm_up lg:max-w-(--breakpoint-md) xl:max-w-(--breakpoint-lg) 2xl:max-w-(--breakpoint-xl)`}
      >
        <div class="head mr-5 md:mr-0">
          <p class="w-full pb-8 text-center md:hidden">
            Violation of Media Freedom
          </p>
          <button
            type="button"
            aria-label="Close"
            class="absolute right-4 top-2 -mr-4 flex h-11 w-11 cursor-pointer items-center justify-center rounded text-5xl leading-none hover:bg-slate-100 dark:hover:bg-slate-800 md:mr-0"
            onClick={() => closeFunction()}
          >
            {/* On big screens details page looks like a modal, we use a close icon. On mobile it's full screen, we use home icon. */}
            <span class="hidden md:inline" aria-hidden="true">
              &times;
            </span>
            <HomeIcon class="h-8 w-8 md:hidden" aria-hidden="true" />
          </button>
          <span class="absolute right-4 top-14 -mr-4 flex w-11 flex-col md:mr-0 md:mt-12">
            <button
              type="button"
              class="flex h-11 w-11 cursor-pointer items-center justify-center rounded hover:bg-slate-100 dark:hover:bg-slate-800"
              title="Copy link to clipboard"
              aria-label="Copy link to clipboard"
              onClick={() => {
                if (navigator.clipboard) {
                  navigator.clipboard
                    .writeText(post.share_url)
                    .then(() =>
                      toast.success("The URL was copied to the clipboard!"),
                    )
                    .catch(() =>
                      toast.error(
                        "Error copying to the clipboard. Please copy the URL from the browser location bar. Sorry for the inconvenience.",
                      ),
                    );
                  return;
                }
                copy(post.share_url);
                toast.success("The URL was copied to the clipboard!");
              }}
            >
              <LinkIcon class="h-7 w-7" aria-hidden="true" />
            </button>
            <SharingButtons url={post.share_url} text={post.share_text} />
            <a
              href={`/desk/alerts/${post.id}`}
              target="_blank"
              rel="noreferrer"
              class="flex h-11 w-11 items-center justify-center rounded hover:bg-slate-100 dark:hover:bg-slate-800"
              title="Edit this Alert (Login required)"
              aria-label="Edit this Alert (login required, opens in new tab)"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                class="h-7 w-7"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
                />
              </svg>
            </a>
          </span>
          <p class="flex flex-wrap">
            <span class="block flex-1 text-left text-steel-blue-600 text-base tracking-wide dark:text-steel-blue-300">
              <span class="font-semibold uppercase">{post.country}</span>
              {" | "}
              <span class="font-semibold">{post.shortDate}</span>
              {" | "}
              {post.type_of_incident_leaves.join(", ")}
            </span>
          </p>
          <h2 id="details-view-title">
            <span class="mt-4 block text-center font-extrabold leading-8 text-gray-900 text-xl tracking-tight dark:text-gray-200">
              {post.title}
            </span>
          </h2>
        </div>
        <div class="body overflow-y-auto">
          {/* in the scrolling part: in the header, the long archive note left phones little room */}
          {post.isRemoved && (
            <div class="rounded border-red-600 border-4 px-2 py-1 -mx-1 mt-4">
              <strong>
                REMOVED - This alert is no longer active and is not included in
                the Mapping Media Freedom data.
              </strong>
              <br />
              {post.removal_reason}
            </div>
          )}
          {post.isHistorical && (
            <div class="rounded border-amber-600 border-4 px-2 py-1 -mx-1 mt-4">
              <strong>ARCHIVED: This alert was archived.</strong>
              <br />
              This alert was created before ECPMF took over the Mapping Media
              Freedom database from Index on Censorship in 2019. This means that
              the alert was written under the lead of a different organisation,
              with a different country focus, methodology, and category system,
              and might not be comparable to recent alerts monitored within the
              Media Freedom Rapid Response (MFRR) project. The alerts remain
              accessible to ensure transparency and allow access to this data,
              but are no longer active and are not included published statistics
              or annual reports. Please note that ECPMF and MFRR do not assume
              responsibility for the content of these alerts. If you would like
              to request the removal of an alert or have additional questions,
              please reach out to mfrr-monitoring@ecpmf.eu.
            </div>
          )}
          <MarkdownText
            text={post.content}
            extraClasses="mt-8"
            headingOffset={2}
          />
          <div class="mt-8 text-steel-blue-600 dark:text-steel-blue-300">
            <p>
              Date of incident: <strong>{post.shortDate}</strong>
              {!post.has_date_of_incidence && (
                <small>
                  {"  "}
                  (No date was specified. This is the date of the first
                  publication here.)
                </small>
              )}
            </p>
            {post.attacked_count_number > 0 && (
              <p>
                Number of attacked persons or entities related to media:{" "}
                <strong>{post.attacked_count_number}</strong>
              </p>
            )}
          </div>
          <div class="mt-8">
            {post.type_of_incident?.length > 0 && (
              <>
                <h3 class="mt-8 font-bold">Type of incident</h3>
                <NestedTags
                  topSlug="type-of-incident"
                  tags={post.type_of_incident}
                />
              </>
            )}
            {post.source_of_incident?.length > 0 && (
              <>
                <h3 class="mt-4 font-bold">Source of incident</h3>
                <NestedTags
                  topSlug="source-of-incident"
                  tags={post.source_of_incident}
                />
              </>
            )}
            {post.context_of_incident?.length > 0 && (
              <>
                <h3 class="mt-4 font-bold">Context of incident</h3>
                <NestedTags
                  topSlug="context-of-incident"
                  tags={post.context_of_incident}
                />
              </>
            )}
            <SubjectsView post={post} />
            {post.specific_topic?.length && (
              <>
                <h3 class="mt-4 font-bold">Specific topic</h3>
                <NestedTags
                  topSlug="specific-topic"
                  tags={post.specific_topic}
                />
              </>
            )}
            {post.project?.length && (
              <>
                <h3 class="mt-4 font-bold">Projects</h3>
                <NestedTags topSlug="project" tags={post.project} />
              </>
            )}
          </div>
          {!!post.videoId && (
            <div>
              <h3 class="mb-4 mt-8 font-bold text-lg">Video</h3>
              {[post.videoId].map((url) => (
                <a href={url} key={url} target="_blank" rel="noreferrer">
                  {url}
                </a>
              ))}
            </div>
          )}
          <div class="mt-8">
            <h3 class="mt-8 font-bold text-lg">Location</h3>
            <p class="mb-4 ml-2">{uniq(post.regionNames).join(" → ")}</p>
            <MiniMap
              loc={post.loc}
              tooltipSuffix={uniq(post.regionNames).join(" → ")}
            />
          </div>
          <h3 class="mt-8 font-bold text-lg">Verification sources</h3>
          {post.news_source_links?.length > 0 && (
            <>
              <h4 class="mt-4 font-bold">News sources</h4>
              <MarkdownText
                text={post.news_source_links}
                headingOffset={4}
                extraClasses="wrap-break-word"
              />
            </>
          )}
          {post.internet_source_links?.length > 0 && (
            <>
              <h4 class="mt-4 font-bold">Internet sources</h4>
              <MarkdownText
                text={post.internet_source_links}
                headingOffset={4}
                extraClasses="wrap-break-word"
              />
            </>
          )}
          {(post.verification_individual_count || 0) > 0 && (
            <p class="mt-4 font-bold">
              Number of individual contacts:{" "}
              <strong>{post.verification_individual_count}</strong>
            </p>
          )}
          <ExternalResourceLinks post={post} />
        </div>
        <div class="relative bottom-0 grid select-none grid-cols-3 gap-x-2 px-4 pt-4">
          {/* the result summary with its status message is inert behind the dialog */}
          <p
            role="status"
            class={`col-span-3 text-center text-red-700 dark:text-red-300 ${store.postLoadError ? "pb-2" : ""}`}
          >
            {store.postLoadError}
          </p>
          {!previousPost ? (
            <div />
          ) : (
            <button
              type="button"
              class="relative bottom-0 cursor-pointer truncate rounded px-1 text-center text-xl hover:bg-slate-100 dark:hover:bg-slate-800"
              onClick={() => store.setPost(previousPost.id)}
              aria-label="Previous alert"
              aria-describedby="details-view-previous"
            >
              Previous
              <br />
              <span id="details-view-previous">
                <MiniPreview post={previousPost} />
              </span>
            </button>
          )}
          <button
            type="button"
            class="relative bottom-0 cursor-pointer truncate rounded px-1 text-center text-xl hover:bg-slate-100 dark:hover:bg-slate-800"
            onClick={() => closeFunction()}
            aria-label="Close"
            aria-describedby="details-view-position"
          >
            Close
            <br />
            {position !== undefined &&
              position !== -1 &&
              total &&
              total > 0 && (
                <span id="details-view-position" class={"text-xs"}>
                  {position + 1} of {total} alerts
                </span>
              )}
          </button>
          {!nextPost ? (
            <div />
          ) : (
            <button
              type="button"
              class="relative bottom-0 cursor-pointer truncate rounded px-1 text-center text-xl hover:bg-slate-100 dark:hover:bg-slate-800"
              onClick={() => store.setPost(nextPost.id)}
              aria-label="Next alert"
              aria-describedby="details-view-next"
            >
              Next
              <br />
              <span id="details-view-next">
                <MiniPreview post={nextPost} />
              </span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
});
